import { Injectable } from '@angular/core';
import { CardioCategory, CardioProgram, ICardioProgramDetail, User } from '../models';
import { HelperService } from './helper.service';
import { DatabaseService } from './database.service';
import { StorageService } from './storage.service';
import { ThenableReference } from '@angular/fire/database';
import { LocalAttachment } from './camera.service';

@Injectable({
    providedIn: 'root'
})
  

export class CardioProgramService {


  constructor(
    private dbService: DatabaseService,
    private storageService: StorageService,
    private helperService: HelperService,
  ) { }

  async getCardioProgramAsPromise(userId: string = null, programId: string): Promise<CardioProgram> {
    const snap = await this.dbService.getSnapshot(`/cardioPrograms/${userId}/${programId}`);
    return snap? new CardioProgram({
      ...snap,
      id: programId
    }) : null;
  }

  getCardioProgramCommentsList(userId: string, planId: string) {
    return this.dbService.getListByRefTest(`/cardioPrograms/${userId}/${planId}/comments`);
  }

  AddCardioProgramComment(userId, programId, comment) {
    return this.dbService.pushByRef(`/cardioPrograms/${userId}/${programId}/comments`, comment);
  }

  async addCardioProgramCommentAttachment(
    userId,
    reportId,
    user: User,
    attachment: LocalAttachment,
    organization?: string
  ) {
    const storagePath = `users/${userId}/cardioPrograms/${reportId}/comments/${attachment.name}`;
    let attachmentRef = null;

    if(attachment.file) {
      attachmentRef = await this.storageService.fbPutFile(storagePath, attachment.file);
    }

    if(attachment.nativePath) {
      attachmentRef = await this.storageService.fpPutNativeFile(storagePath, attachment.nativePath);
    }

    if(!attachmentRef) {
      throw new Error('No attachment reference found');
    }

    return this.AddCardioProgramComment(userId, reportId, {
      comment: null,
      creationDate: new Date().toISOString(),
      createdBy: user.uid,
      createdByName: user.getFullName(),
      organization: organization || null,
      attachments: [{
        url: attachmentRef,
        fileName: attachment.name,
        type: attachment.type,
      }]
    });
  }  

  updateCardioProgramComment(userId, programId, commentId: string, comment) {
    return this.dbService.updateRef(`/cardioPrograms/${userId}/${programId}/comments/${commentId}`, comment);
  }

  deleteCardioProgramComment(userId, programId, commentId: string) {
    return this.dbService.removeRef(`/cardioPrograms/${userId}/${programId}/comments/${commentId}`);
  }

  deleteCardioProgram(userId: string, programId: string) {
    return this.dbService.removeRef(`/cardioPrograms/${userId}/${programId}`);
  }

  getUserCardioProgramList(userId: string) {
    return this.dbService.getListByRefTest<CardioProgram>(`cardioPrograms/${userId}`);
  }

  async saveUserCardioProgram(orgId: string, userId, programId, cardioProgramDetail: ICardioProgramDetail, modifiedBy: string): Promise<ThenableReference> {
    let result;
    let cardioProgram = {
      draftCardioProgram: cardioProgramDetail,
      lastModifiedBy: modifiedBy,
      lastModifiedDate: new Date().toISOString(),
      organization: orgId || null,     
    };
    if(programId === "add") {
      result = await this.dbService.pushByRef(`/cardioPrograms/${userId}`, {
        ...cardioProgram,
        createdBy: modifiedBy,
        creationDate: new Date().toISOString(),
        readNotes:false,
      });
    } else {
      result = await this.dbService.updateRef(`/cardioPrograms/${userId}/${programId}`, cardioProgram);
    }
    return result
  }

  async publishUserCardioProgram(orgId: string, userId, programId, cardioProgramDetail: ICardioProgramDetail, modifiedBy: string, notification = true): Promise<ThenableReference> {
    let result;
    let cardioProgram: Partial<CardioProgram> = {
      ...cardioProgramDetail,
      lastModifiedBy: modifiedBy,
      lastModifiedDate: new Date().toISOString(),
      draftCardioProgram: null,
    }
    if(notification) {
      cardioProgram.published = new Date().toISOString();
    }
    if(programId === "add") {
      result = await this.dbService.pushByRef(`/cardioPrograms/${userId}`, {
        ...cardioProgram,
        organization: orgId || null,
        createdBy: modifiedBy,
        creationDate: new Date().toISOString(),
      });
    } else {
      result = await this.dbService.updateRef(`/cardioPrograms/${userId}/${programId}`, cardioProgram);
    }
    return result
  }

  updateUserCardioProgram(userId: string, programId: string, data: Partial<ICardioProgramDetail>){
    return this.dbService.updateRef(`/cardioPrograms/${userId}/${programId}`, data);
  } 

  updateUserCardioProgramDraft(userId: string, programId: string, data: Partial<ICardioProgramDetail>){
    return this.dbService.updateRef(`/cardioPrograms/${userId}/${programId}/draftCardioProgram`, data);
  } 

  async getCardioProgramsAsPromise(userId): Promise<CardioProgram[]> {
    const snap = await this.dbService.getSnapshot(`/cardioPrograms/${userId}`);
    let programs = this.helperService.transformFirebaseObjectToArray(snap);
    return programs.map((program) => {
      return new CardioProgram(program)
    });
  }

  async getPublishedCardioPrograms(userId: string) {
    let programs = await this.getCardioProgramsAsPromise(userId);
    return programs.filter((program: CardioProgram) => {
      return program.isPublished() || program.createdBy === userId;
    }).sort((a: CardioProgram, b: CardioProgram) => {
      return +new Date(b.startDate) - +new Date(a.startDate);
    });;
  }

  /* Organization Cardio Programs */
  getOrganizationCardioProgramList(orgId: string) {
    return this.dbService.getListByRefTest(`organizationCardioPrograms/${orgId}`);
  }

  async getOrganizationCardioProgramsAsPromise(orgId: string): Promise<CardioProgram[]> {
    const snap = await this.dbService.getSnapshot(`organizationCardioPrograms/${orgId}`);
    return snap? this.helperService.transformFirebaseObjectToArray(snap).map(mp => {return new CardioProgram(mp)}) : null;
  }

  async getOrganizationCardioProgramAsPromise(organizationId: string, programId: string): Promise<CardioProgram> {
    const snap = await this.dbService.getSnapshot(`organizationCardioPrograms/${organizationId}/${programId}`);
    return snap? new CardioProgram({id: programId, ...snap}) : null;
  }

  async saveOrganizationCardioProgram(orgID: string, programId: string, planDetail: ICardioProgramDetail, modifiedBy: string): Promise<ThenableReference> {
    let result;
    let protocol = {
      lastModifiedBy: modifiedBy,
      lastModifiedDate: new Date().toISOString(),
      organization: orgID,
      draftCardioProgram: planDetail
    };
    if(programId === "add") {
      result = await this.dbService.pushByRef(`/organizationCardioPrograms/${orgID}`, {
        ...protocol,
        createdBy: modifiedBy,
        creationDate: new Date().toISOString(),
        organization: orgID,
      });
    } else {
      result = await this.dbService.updateRef(`/organizationCardioPrograms/${orgID}/${programId}`, protocol);
    }
    return result
  }

  async publishOrganizationCardioProgram(orgID: string, programId: string, mealPlanDetail: ICardioProgramDetail, modifiedBy: string): Promise<ThenableReference> {
    let result;
    let MP = {
      ...mealPlanDetail,
      lastModifiedBy: modifiedBy,
      lastModifiedDate: new Date().toISOString(),
      draftCardioProgram: null,
      published: new Date().toISOString(),
    };
    if(!programId || programId === "add") {
      result = await this.dbService.pushByRef(`/organizationCardioPrograms/${orgID}`, {
        ...MP,
        createdBy: modifiedBy,
        creationDate: new Date().toISOString(),
        organization: orgID,
      });
    } else {
      result = await this.dbService.updateRef(`/organizationCardioPrograms/${orgID}/${programId}`, MP);
    }
    return result
  }

  deleteOrganizationCardioProgram(orgId: string, mealplanId: string) {
    return this.dbService.removeRef(`organizationCardioPrograms/${orgId}/${mealplanId}`);
  }

  getTrackedCardioCommentsList(userId: string, planId: string) {
    return this.dbService.getListByRefTest(`/cardioTracking/${userId}/${planId}/comments`);
  }

  addTrackedCardioComment(userId, programId, comment) {
    return this.dbService.pushByRef(`/cardioTracking/${userId}/${programId}/comments`, comment);
  }

  deleteTrackedCardioComment(userId, programId, commentId) {
    return this.dbService.removeRef(`/cardioTracking/${userId}/${programId}/comments/${commentId}`);
  }

  async addTrackedCardioCommentAttachment(
    userId,
    reportId,
    user: User,
    attachment: LocalAttachment,
    organization?: string
  ) {
    const storagePath = `users/${userId}/cardioTracking/${reportId}/comments/${attachment.name}`;
    let attachmentRef = null;

    if(attachment.file) {
      attachmentRef = await this.storageService.fbPutFile(storagePath, attachment.file);
    }

    if(attachment.nativePath) {
      attachmentRef = await this.storageService.fpPutNativeFile(storagePath, attachment.nativePath);
    }

    if(!attachmentRef) {
      throw new Error('No attachment reference found');
    }

    return this.addTrackedCardioComment(userId, reportId, {
      comment: null,
      creationDate: new Date().toISOString(),
      createdBy: user.uid,
      createdByName: user.getFullName(),
      organization: organization || null,
      attachments: [{
        url: attachmentRef,
        fileName: attachment.name,
        type: attachment.type,
      }]
    });
  }  

  // Categories
    getOrganizationCardioCategoryList(organizationId: string) {
      return this.dbService.getListByRef<CardioCategory>(`organizationCardioCategories/${organizationId}`, "id");
    }
  
    
    async getOrganizationCardioCategoriesAsPromise(orgId: string): Promise<CardioCategory[]> {
      const snap = await this.dbService.getSnapshot(`organizationCardioCategories/${orgId}`);
      return snap? this.helperService.transformFirebaseObjectToArray(snap) : null;
    }
  
    async getOrganizationCardioCategoryAsPromise(organizationId: string, categoryId: string): Promise<CardioCategory> {
      const snap = await this.dbService.getSnapshot(`organizationCardioCategories/${organizationId}/${categoryId}`);
      return snap? {id: categoryId, ...snap} : null;
    }
  
    async publishOrganizationCardioCategory(orgID: string, programId: string, category: Partial<CardioCategory>, modifiedBy: string): Promise<ThenableReference> {
      let result;
      let SC = {
        ...category,
        lastModifiedBy: modifiedBy,
        lastModifiedDate: new Date().toISOString(),
      };
      if(!programId || programId === "add") {
        result = await this.dbService.pushByRef(`/organizationCardioCategories/${orgID}`, {
          createdBy: modifiedBy,
          creationDate: new Date().toISOString(),
          organization: orgID,
          ...SC
        })
      } else {
        result = await this.dbService.updateRef(`/organizationCardioCategories/${orgID}/${programId}`, SC)
      }
      return result
    }
  
    deleteOrganizationCardioCategory(organizationId: string, categoryId: string): Promise<void> {
      return this.dbService.removeRef(`/organizationCardioCategories/${organizationId}/${categoryId}`);
    }
}
